import { GalleryAnimateZoomDOM } from './gallery-animate-zoom-dom.js';
import { renderFilterButtons } from './filter-ui.js';
import { resetCrumbTracking } from './gallery-breadcrumb.js';
import { clusterLayout, CLUSTER_ITEM_SIZE as SIZE } from './accessory-cluster-layout.js';

// V2 keeps the existing data, thumbnail layers, lightbox and input handling.
// Only layout, 2D visibility and framing replace the original row assumptions.
export class GalleryAccessoryClusters extends GalleryAnimateZoomDOM {
    async init() {
        this._motionToken = 0;
        this._onResize = () => this.onResize();
        window.addEventListener('resize', this._onResize);
        await this.loadData();
        if (this._destroyed) return;
        this.buildLayout();
        this.bindEvents();
        this.checkUrlParam();
        this._syncWorld();
        this._wake();
    }

    destroy() {
        this._stopAnimation();
        super.destroy();
    }

    buildLayout() {
        const byCollection = new Map();
        for (const item of this.data) {
            const key = item.collection || item.expand?.collection?.id;
            if (!byCollection.has(key)) byCollection.set(key, []);
            byCollection.get(key).push(item);
        }
        const source = [...this.collMap].filter(([key]) => byCollection.has(key))
            .map(([key, c]) => ({ key, label: c.name || c.title || 'Archive',
                count: byCollection.get(key).length, items: byCollection.get(key) }));
        const layout = clusterLayout(source);
        this.worldW = layout.width;
        this.worldH = layout.height;
        this._collectionList = source.map(({ key, label }) => ({ key, label }));
        this._world = document.createElement('div');
        this._world.className = 'gallery-dom-world accessory-clusters-world';
        this._world.style.width = `${this.worldW}px`;
        this._world.style.height = `${this.worldH}px`;
        this.items = [];
        this.groups = layout.groups.map(cluster => {
            const el = document.createElement('section');
            el.className = 'accessory-cluster';
            el.dataset.season = cluster.label;
            const group = { ...cluster, el, items: byCollection.get(cluster.key) };
            const title = document.createElement('button');
            title.type = 'button';
            title.className = 'accessory-cluster-label';
            title.style.left = `${group.x}px`;
            title.style.top = `${group.y}px`;
            title.textContent = group.label.toUpperCase();
            title.setAttribute('aria-label', `View ${title.textContent}`);
            title.addEventListener('pointerdown', e => e.stopPropagation());
            title.addEventListener('click', () => this.setFilter(group.key));
            el.appendChild(title);
            group.titleEl = title;
            const categoryEls = new Map((group.subgroups || []).map(subgroup => {
                const categoryEl = document.createElement('div');
                categoryEl.className = 'accessory-subcluster';
                categoryEl.dataset.category = subgroup.key;
                categoryEl.setAttribute('role', 'group');
                categoryEl.setAttribute('aria-label', `${group.label} ${subgroup.label}`);
                el.appendChild(categoryEl);
                return [subgroup.key, categoryEl];
            }));
            group.items.forEach((item, i) => {
                const point = group.points[i];
                item._category = point.category;
                item._categoryEl = categoryEls.get(point.category);
                item._x = group.x + point.x - SIZE / 2;
                item._y = group.y + point.y - SIZE / 2;
                item._h = SIZE;
                item._dw = point.width;
                item._dh = point.height;
                item._dx = (SIZE - item._dw) / 2;
                item._dy = (SIZE - item._dh) / 2;
                item._collKey = group.key;
                item._group = group;
                item._el = null;
                item._loadedTier = -1;
                this.items.push(item);
            });
            this._world.appendChild(el);
            return group;
        });
        this.items.forEach((item, index) => this.lbIndex.set(item.id, index));
        this.viewport.appendChild(this._world);
        this._setZoomRange();
        Object.assign(this, this._framedView());
        this._renderFilterButtons();
        this._updateScene();
    }

    _setZoomRange() {
        this.fitZ = this._framedView(this.groups.find(g => g.key === this._activeFilter)).zoom;
        this.ZOOM_MIN = this.fitZ * 0.6;
        // V2 also uses the DOM renderer on desktop, where mouse users need
        // more detail than the inherited touch-device zoom ceiling allows.
        this.ZOOM_MAX = window.matchMedia('(hover: hover) and (pointer: fine)').matches ? 4 : 1.7;
        this.labelFullZ = 0.9;
        this.ZOOM_LEVELS = [this.fitZ, this.fitZ * 2, 0.9, this.ZOOM_MAX];
    }

    _framedView(group) {
        const width = group ? group.radius * 2 : this.worldW;
        const height = group ? group.radius * 2 : this.worldH;
        const zoom = Math.min(1.25, (this.W - 64) / Math.max(1, width),
            (this.H - 160) / Math.max(1, height));
        return { zoom, panX: this.W / 2 - (group?.x ?? this.worldW / 2) * zoom,
            panY: this.H / 2 - (group?.y ?? this.worldH / 2) * zoom };
    }

    onResize() {
        this._stopAnimation();
        this.W = window.innerWidth;
        this.H = window.innerHeight;
        this._setZoomRange();
        Object.assign(this, this._framedView(this.groups.find(g => g.key === this._activeFilter)));
        this._lastPanKey = '';
        this._syncWorld();
        this._wake();
    }

    _mountItem(item) {
        super._mountItem(item);
        if (!item._el) return;
        item._el.style.top = `${item._y + item._dy}px`;
        // The shared renderer's full-size white tile can cover a smaller nearby
        // object. V2's white page only needs the scaled image itself.
        item._frameEl?.remove();
        item._frameEl = null;
        item._categoryEl?.appendChild(item._el);
        item._el.dataset.category = item._category;
        item._el.tabIndex = 0;
        item._el.setAttribute('role', 'button');
        item._el.setAttribute('aria-label', `Open ${item.title}`);
        item._imgA.alt = item.title;
        item._imgB.alt = '';
        item._imgB.setAttribute('aria-hidden', 'true');
        item._el.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.openLightbox(item.id);
            }
        });
    }

    _isOnScreen(item, margin = 0) {
        const x = (item._x + item._dx) * this.zoom + this.panX;
        const y = (item._y + item._dy) * this.zoom + this.panY;
        return x < this.W + margin && x + item._dw * this.zoom > -margin
            && y < this.H + margin && y + item._dh * this.zoom > -margin;
    }

    _groupOffset() { return 0; }

    itemAt(sx, sy) {
        const wx = (sx - this.panX) / this.zoom;
        const wy = (sy - this.panY) / this.zoom;
        let nearest = null, distance = Infinity;
        for (const item of this.items) {
            if (!this._itemVisible(item)) continue;
            const dx = wx - (item._x + SIZE / 2), dy = wy - (item._y + SIZE / 2);
            // Keep tiny jewellery selectable in overview without letting the
            // old 220px hit rectangle intercept neighbouring objects.
            const halfW = Math.max(item._dw, 24 / this.zoom) / 2;
            const halfH = Math.max(item._dh, 24 / this.zoom) / 2;
            if (Math.abs(dx) <= halfW && Math.abs(dy) <= halfH && dx * dx + dy * dy < distance) {
                nearest = item;
                distance = dx * dx + dy * dy;
            }
        }
        return nearest;
    }

    _updateScene() {
        if (this._destroyed || !this.items.length) return;
        const moving = this._sceneInMotion();
        this._tierLoadQueue.length = 0;
        let mounts = 0, pending = false;
        for (const item of this.items) {
            if (!this._itemVisible(item)) {
                if (item._el) this._unmountItem(item);
                continue;
            }
            if (!this._isOnScreen(item, 120)) {
                if (!moving && item._el) this._unmountItem(item);
                continue;
            }
            if (!item._el) {
                if (mounts >= (moving ? 2 : 6)) { pending = true; continue; }
                this._mountItem(item);
                mounts++;
            }
            if (moving) continue;
            const extent = Math.max(item._dw, item._dh) * this.zoom;
            const tier = extent < 80 ? 0 : extent < 200 ? 1 : 2;
            const target = this._isOnScreen(item) ? tier : Math.min(tier, 1);
            if (item._loadedTier >= target) continue;
            const url = this.thumbUrl(item, target);
            const spare = item._curImg === item._imgA ? item._imgB : item._imgA;
            if (url && spare?.getAttribute('src') !== url) {
                this._tierLoadQueue.push({ item, tier: target, url });
            }
        }
        if (!moving) this._drainTierLoadQueue();
        if (pending && !this._sceneFillTimer) {
            this._sceneFillTimer = setTimeout(() => {
                this._sceneFillTimer = null;
                this._updateScene();
            }, 32);
        }
    }

    _syncWorld() {
        if (!this._world) return;
        this.clamp();
        this._world.style.transform = `translate3d(${this.panX}px, ${this.panY}px, 0) scale(${this.zoom})`;
        // Keep labels readable on overview without growing them into the
        // surrounding images. Their centre position never moves with zoom.
        const labelScale = Math.min(1.25, Math.max(0.55, 14 / (44 * this.zoom)));
        this._world.style.setProperty('--cluster-label-scale', labelScale);
    }

    clamp() {
        const group = this.groups.find(g => g.key === this._activeFilter);
        const left = group ? group.x - group.radius : 0;
        const right = group ? group.x + group.radius : this.worldW;
        const top = group ? group.y - group.radius : 0;
        const bottom = group ? group.y + group.radius : this.worldH;
        this.panX = Math.max(80 - right * this.zoom, Math.min(this.W - 80 - left * this.zoom, this.panX));
        this.panY = Math.max(80 - bottom * this.zoom, Math.min(this.H - 80 - top * this.zoom, this.panY));
    }

    bindEvents() {
        super.bindEvents();
        this.viewport.addEventListener('pointerdown', () => this._stopAnimation(), { capture: true });
        this.viewport.addEventListener('wheel', () => this._stopAnimation(), { capture: true });
    }

    _stopAnimation() {
        this._motionToken++;
        if (this._viewFrame) cancelAnimationFrame(this._viewFrame);
        this._viewFrame = null;
        this._zoomAnimating = false;
    }

    _animateView(target, onComplete, animate = true) {
        this._stopAnimation();
        const token = this._motionToken;
        this.velX = this.velY = this.zoomVel = 0;
        const from = { zoom: this.zoom, panX: this.panX, panY: this.panY };
        const duration = animate && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 450 : 0;
        const start = performance.now();
        this._zoomAnimating = !!duration;
        const tick = now => {
            if (this._destroyed || token !== this._motionToken) return;
            const t = duration ? Math.min(1, (now - start) / duration) : 1;
            const ease = 1 - (1 - t) ** 3;
            for (const k of ['zoom', 'panX', 'panY']) this[k] = from[k] + (target[k] - from[k]) * ease;
            this._syncWorld();
            this._wake();
            if (t < 1) this._viewFrame = requestAnimationFrame(tick);
            else {
                this._viewFrame = null;
                this._zoomAnimating = false;
                this._lastPanKey = '';
                this._wake();
                onComplete?.();
            }
        };
        tick(start);
    }

    animateZoom(targetZ, cx, cy, onComplete) {
        const zoom = Math.max(this.ZOOM_MIN, Math.min(this.ZOOM_MAX, targetZ));
        const ratio = zoom / this.zoom;
        this._animateView({ zoom, panX: cx - ratio * (cx - this.panX),
            panY: cy - ratio * (cy - this.panY) }, onComplete);
    }

    _renderFilterButtons() {
        renderFilterButtons(document.getElementById('gallery-filter'), this._collectionList,
            this._activeFilter, key => this.setFilter(key));
    }

    setFilter(key, { updateUrl = true } = {}) {
        const group = this.groups.find(g => g.key === key);
        if (key !== 'all' && !group) return;
        this._stopAnimation();
        this.velX = this.velY = this.zoomVel = 0;
        this._activeFilter = key;
        for (const season of this.groups) season.el.hidden = key !== 'all' && season.key !== key;
        resetCrumbTracking(this);
        this._renderFilterButtons();
        this._setZoomRange();
        // Season selection is an immediate visibility change and snap, just
        // like the main collection filters. Pan/zoom controls keep their motion.
        Object.assign(this, this._framedView(group));
        this._lastPanKey = '';
        this._syncWorld();
        this._updateScene();
        this._wake();
        if (updateUrl) {
            const url = new URL(location.href);
            if (group) url.searchParams.set('filter', group.label.toLowerCase());
            else url.searchParams.delete('filter');
            url.searchParams.delete('item');
            url.hash = '';
            history.replaceState(null, '', url.pathname + url.search);
        }
    }

    checkUrlParam() {
        const filter = new URLSearchParams(location.search).get('filter');
        const group = this.groups.find(g => g.label.toLowerCase() === filter?.toLowerCase() || g.key === filter);
        if (group) this.setFilter(group.key, { updateUrl: false });
        super.checkUrlParam();
    }

    openLightbox(id) {
        return super.openLightbox(id, this.items.filter(item => this._itemVisible(item)));
    }

    navigateTo(id, openLb = true, animate = true) {
        const item = this.items.find(i => i.id === id);
        if (!item) return;
        if (!this._itemVisible(item)) this.setFilter(item._collKey);
        const zoom = Math.max(this.zoom, 0.9);
        this._animateView({ zoom, panX: this.W / 2 - (item._x + SIZE / 2) * zoom,
            panY: this.H / 2 - (item._y + SIZE / 2) * zoom },
        openLb ? () => this.openLightbox(id) : null, animate);
    }
}
